import React, { useState, useContext, useEffect } from "react";
import { MdOutlineStorefront } from "react-icons/md";
import { ImSpinner8 } from "react-icons/im";
import { ProfileContext } from "../../../../store/ProfileProvider";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import localizedFormat from "dayjs/plugin/localizedFormat";
import relativeTime from "dayjs/plugin/relativeTime";
import DisplayAmountWithDecimals from "../../shared/DisplayAmountWithDecimals";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);

type TicketListProps = {
  ticketList: any;
  ticketClosedList: any;
  activeTab: "open" | "closed"; // Add activeTab prop
  activeToNewTicketId: string;
  setActiveToNewTicketId: any;
  setTradeDetail: any;
  setOfferDetail: any;
  isFetchingList: boolean;
  hasMoreOpenTickets?: boolean;
  hasMoreClosedTickets?: boolean;
  isLoadingMoreOpen?: boolean;
  isLoadingMoreClosed?: boolean;
  onLoadMoreOpen?: () => void;
  onLoadMoreClosed?: () => void;
  setIsChatLoading: any;
};

const TicketList: React.FC<TicketListProps> = ({
  ticketList,
  ticketClosedList,
  activeTab, // Receive activeTab from parent
  activeToNewTicketId,
  setActiveToNewTicketId,
  setTradeDetail,
  setOfferDetail,
  isFetchingList,
  hasMoreOpenTickets = false,
  hasMoreClosedTickets = false,
  isLoadingMoreOpen = false,
  isLoadingMoreClosed = false,
  onLoadMoreOpen,
  onLoadMoreClosed,
  setIsChatLoading
}) => {
  const { profileTimeZone } = useContext(ProfileContext);
  const [activeId, setActiveId] = useState("");
  const userRole = localStorage.getItem("userRole")?.toLowerCase();
  const [timestampUpdates, setTimestampUpdates] = useState(0);

  // Get current list and pagination state based on activeTab from parent
  const currentList = activeTab === "open" ? ticketList : ticketClosedList;
  const hasMoreTickets = activeTab === "open" ? hasMoreOpenTickets : hasMoreClosedTickets;
  const isLoadingMore = activeTab === "open" ? isLoadingMoreOpen : isLoadingMoreClosed;
  const onLoadMore = activeTab === "open" ? onLoadMoreOpen : onLoadMoreClosed;

  // Reset active ticket when tab changes
  useEffect(() => {
    setActiveId("");
  }, [activeTab]);

  useEffect(() => {
    if(activeToNewTicketId && activeToNewTicketId !== "" && userRole !== "superadmin") {
      const newTradeComeIn = ticketList.find((x: any) => x.id === activeToNewTicketId);
      setTradeDetail(newTradeComeIn);
      handleTradeDetailForChat(newTradeComeIn);
      setActiveId(newTradeComeIn.id);
    }
  }, [activeToNewTicketId]);  // eslint-disable-line

  const formatChatTimestamp = (timestamp: string | number, userTimeZone: string) => {
    // Parse timestamp - handle both string and number formats
    let date;

    if (typeof timestamp === 'string') {
      // If it's a numeric string (Unix timestamp), convert to number
      if (/^\d+$/.test(timestamp)) {
        const numTimestamp = parseInt(timestamp);
        // Check if it's in seconds (10 digits) or milliseconds (13 digits)
        date = dayjs(numTimestamp < 10000000000 ? numTimestamp * 1000 : numTimestamp).tz(userTimeZone);
      } else {
        // ISO string or other date format
        date = dayjs(timestamp).tz(userTimeZone);
      }
    } else {
      // Handle numeric timestamp
      date = dayjs(timestamp < 10000000000 ? timestamp * 1000 : timestamp).tz(userTimeZone);
    }

    // Get current time in user's timezone
    const now = dayjs().tz(userTimeZone);

    // Validate parsed date
    if (!date.isValid()) {
      console.error('Invalid timestamp:', timestamp);
      return 'Invalid date';
    }

    // Calculate differences
    const diffInMinutes = now.diff(date, "minute");
    const diffInHours = now.diff(date, "hour");
    const diffInDays = now.diff(date, "day");

    // Format based on time difference
    if (diffInMinutes < 1) {
      return "Just now";
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} min ago`;
    } else if (diffInHours < 24 && date.isSame(now, "day")) {
      return date.format("h:mm A");
    } else if (diffInDays === 1) {
      return `Yesterday ${date.format("h:mm A")}`;
    } else if (diffInDays > 1 && diffInDays < 7) {
      return date.format("ddd h:mm A");
    } else if (date.isSame(now, "year")) {
      return date.format("DD MMM");
    } else {
      return date.format("DD MMM YYYY");
    }
  };

  // Updated getUpdateInterval function to work with user timezone
  const getUpdateInterval = (timestamp: string | number, userTimeZone: string) => {
    const now = dayjs().tz(userTimeZone);

    let time;
    if (typeof timestamp === 'string') {
      if (/^\d+$/.test(timestamp)) {
        const numTimestamp = parseInt(timestamp);
        time = dayjs(numTimestamp < 10000000000 ? numTimestamp * 1000 : numTimestamp).tz(userTimeZone);
      } else {
        time = dayjs(timestamp).tz(userTimeZone);
      }
    } else {
      time = dayjs(timestamp < 10000000000 ? timestamp * 1000 : timestamp).tz(userTimeZone);
    }

    const diffMins = now.diff(time, "minute");

    if (diffMins < 1) return 10_000; // every 10 sec
    if (diffMins < 60) return 60_000; // every 1 min
    if (diffMins < 24 * 60) return 5 * 60_000; // every 5 min
    return null; // no update needed
  };

  // Updated useEffect hook
  useEffect(() => {
    let shortestInterval: number | null = null;

    for (const item of currentList) {
      const interval = getUpdateInterval(item.created_at, profileTimeZone);
      if (interval !== null) {
        if (shortestInterval === null || interval < shortestInterval) {
          shortestInterval = interval;
        }
      }
    }

    if (shortestInterval !== null) {
      const timeout = setTimeout(() => {
        setTimestampUpdates((prev) => prev + 1); // trigger re-render
      }, shortestInterval);
      return () => clearTimeout(timeout);
    }
  }, [timestampUpdates, currentList, profileTimeZone]);

  const handleTradeDetailForChat = (detail: any) => {
    try {
      if (!detail) {
        console.warn('handleTradeDetailForChat: detail is null or undefined');
        return;
      }

      // IMMEDIATELY set loading and clear states
      setIsChatLoading(true);
      setOfferDetail(null); // Clear immediately
      setTradeDetail(detail); // This will trigger the TicketChatBox useEffect

      // Set active ID
      if (detail.id !== undefined && detail.id !== null) {
        setActiveId(String(detail.id));
      } else {
        setActiveId("");
      }
      setActiveToNewTicketId("");
      localStorage.setItem("activeToNewTicketIdFromStorage", "");
    } catch (error) {
      console.error('handleTradeDetailForChat: Unexpected error:', error);
      setActiveId("");
      setActiveToNewTicketId("");
      localStorage.setItem("activeToNewTicketIdFromStorage", "");
      setOfferDetail(null);
    }
  };

  const displayStatus = (status: string) => {
    switch (status) {
      case "open":
        return (
          <span className="flex items-center gap-1 leading-[18px] text-green-500">
            open ●
          </span>
        );
      case "closed":
        return (
          <span className="flex items-center gap-1 leading-[18px] text-red-500">
            closed ●
          </span>
        );
      default:
        return (
          <span className="flex items-center gap-1 leading-[18px] text-gray-500">
            {status} ●
          </span>
        );
    }
  };

  return (
    <>
      {(isFetchingList) &&
        <div className="flex items-center justify-center py-8">
          <ImSpinner8 className="spinner text-primary-500" style={{ fontSize: '2.2rem' }} />
        </div>
      }
      {(!isFetchingList && currentList.length === 0) &&
        <div className="flex text-sm justify-center text-gray-600 py-8">
          No {activeTab} tickets available
        </div>
      }
      {!isFetchingList && currentList && currentList.length > 0 &&
        <>
          {currentList.map((item: any, index: number) => {
            const isActive = item.id === activeId;
            return (
              <div
                key={index}
                onClick={() => handleTradeDetailForChat(item)}
                className={`flex cursor-pointer items-center justify-between px-3 py-3 transition ${isActive
                  ? "border-primary-600 active-trade border-l-4 bg-[#F1F5FB] pointer-events-none"
                  : "hover:bg-gray-50"
                  }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`flex h-10 w-10 items-center justify-center rounded-full ${isActive
                      ? "bg-[#838ebc] text-white"
                      : "bg-gray-100 text-gray-600"
                      }`}
                  >
                    <MdOutlineStorefront className="h-5 w-5" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-semibold leading-[18px] text-navy-700">
                      {userRole === "superadmin" && item.subaccount.name}
                      {userRole !== "superadmin" &&
                        <>
                          Trade <DisplayAmountWithDecimals amount={Number(item?.from_amount)} currency={item.from_asset} decimal={item?.from_asset_decimal} /> {item.from_asset}
                        </>
                      }
                    </p>
                    <p className="text-xs font-medium leading-[18px] text-[#313F6F]">
                      #{item.shortid} - {item.from_asset}/{item.to_asset}
                    </p>
                  </div>
                </div>
                <div className="flex flex-col items-end justify-between space-y-1 text-xs text-[#7A7F8C]">
                  {displayStatus(item.conversation_status)}
                  <span className="leading-[18px]">
                    {formatChatTimestamp(item.created_at, profileTimeZone)}
                  </span>
                </div>
              </div>
            );
          })}

          {/* Load More Button */}
          {hasMoreTickets && onLoadMore && (
            <div className="flex items-center justify-center py-4 px-3">
              <button
                onClick={onLoadMore}
                disabled={isLoadingMore}
                className="flex items-center gap-2 px-3 py-2 text-xs font-semibold text-primary-600 border border-gray-300 rounded-md hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                {isLoadingMore ? (
                  <>
                    <ImSpinner8 className="spinner text-primary-600" style={{ fontSize: '1rem' }} />
                    Loading...
                  </>
                ) : (
                  `Load More`
                )}
              </button>
            </div>
          )}
        </>
      }
    </>
  );
};

export default TicketList;